import React, { useState, useEffect } from 'react'
import {
  Plus,
  Save,
  RotateCcw,
  Target,
  DollarSign,
  Users,
  TrendingUp,
  BarChart3,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag,
  HelpCircle
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import autoIncrementService from '../../services/autoIncrementService'
import leadSourceMasterService from '../../services/leadSourceMasterService'

const LeadSourceMasterForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('leadSourceInfo')
  const [showHelp, setShowHelp] = useState(false)
  const [isLoadingLeadSourceCode, setIsLoadingLeadSourceCode] = useState(false)
  const [formData, setFormData] = useState({
    // Lead Source Info - Matching backend model
    leadSourceCode: '',
    leadSourceName: '',
    leadSourceDescription: '',
    leadSourceCategory: 'Digital Marketing',
    leadSourceType: 'Online',
    leadSourceStatus: 'Active',
    isLeadSourceActive: 'A',
    // Cost and ROI tracking
    costPerLead: 0,
    expectedConversionRate: 0,
    averageDealValue: 0,
    // Contact and tracking information
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    // Geographic and demographic targeting
    targetRegions: [],
    targetIndustries: [],
    targetCompanySize: 'All',
    // Performance metrics
    totalLeadsGenerated: 0,
    totalLeadsConverted: 0,
    totalRevenue: 0,
    lastLeadDate: '',
    // Integration and tracking
    trackingCode: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    // Quality and scoring
    leadQualityScore: 5,
    leadVolumeScore: 5,
    costEffectivenessScore: 5,
    // Notes and additional information
    notes: '',
    tags: []
  })

  const [targetRegions, setTargetRegions] = useState([''])
  const [targetIndustries, setTargetIndustries] = useState([''])
  const [tags, setTags] = useState([''])

  // Load lead source code on component mount
  useEffect(() => {
    loadLeadSourceCode()
  }, [])

  const loadLeadSourceCode = async () => {
    try {
      setIsLoadingLeadSourceCode(true)
      const response = await autoIncrementService.getCurrentValue('LEAD-SOURCE')
      if (response.success) {
        const sequence = response.data.formattedValue
        setFormData(prev => ({
          ...prev,
          leadSourceCode: sequence
        }))
      }
    } catch (error) {
      console.error('Error loading lead source code:', error)
      toast.error('Failed to load lead source code')
    } finally {
      setIsLoadingLeadSourceCode(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleTargetRegionChange = (index, value) => {
    const newRegions = [...targetRegions]
    newRegions[index] = value
    setTargetRegions(newRegions)
    setFormData(prev => ({
      ...prev,
      targetRegions: newRegions.filter(region => region.trim() !== '')
    }))
  }

  const addTargetRegion = () => {
    setTargetRegions([...targetRegions, ''])
  }

  const removeTargetRegion = (index) => {
    if (targetRegions.length > 1) {
      const newRegions = targetRegions.filter((_, i) => i !== index)
      setTargetRegions(newRegions)
      setFormData(prev => ({
        ...prev,
        targetRegions: newRegions.filter(region => region.trim() !== '')
      }))
    }
  }

  const handleTargetIndustryChange = (index, value) => {
    const newIndustries = [...targetIndustries]
    newIndustries[index] = value
    setTargetIndustries(newIndustries)
    setFormData(prev => ({
      ...prev,
      targetIndustries: newIndustries.filter(industry => industry.trim() !== '')
    }))
  }

  const addTargetIndustry = () => {
    setTargetIndustries([...targetIndustries, ''])
  }

  const removeTargetIndustry = (index) => {
    if (targetIndustries.length > 1) {
      const newIndustries = targetIndustries.filter((_, i) => i !== index)
      setTargetIndustries(newIndustries)
      setFormData(prev => ({
        ...prev,
        targetIndustries: newIndustries.filter(industry => industry.trim() !== '')
      }))
    }
  }

  const handleTagChange = (index, value) => {
    const newTags = [...tags]
    newTags[index] = value
    setTags(newTags)
    setFormData(prev => ({
      ...prev,
      tags: newTags.filter(tag => tag.trim() !== '')
    }))
  }

  const addTag = () => {
    setTags([...tags, ''])
  }

  const removeTag = (index) => {
    if (tags.length > 1) {
      const newTags = tags.filter((_, i) => i !== index)
      setTags(newTags)
      setFormData(prev => ({
        ...prev,
        tags: newTags.filter(tag => tag.trim() !== '')
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      // Get the next sequence value only when actually saving
      const sequenceResponse = await autoIncrementService.getNextValue('LEAD-SOURCE')
      if (!sequenceResponse.success) {
        toast.error('Failed to generate lead source code')
        return
      }
      
      // Prepare data for submission with the new sequence value
      const submissionData = {
        ...formData,
        leadSourceCode: sequenceResponse.data.sequence, // Use the incremented value
        targetRegions: formData.targetRegions.filter(region => region.trim() !== ''),
        targetIndustries: formData.targetIndustries.filter(industry => industry.trim() !== ''),
        tags: formData.tags.filter(tag => tag.trim() !== '')
      }

      const response = await leadSourceMasterService.createLeadSource(submissionData)
      
      if (response.success) {
        toast.success('Lead source created successfully')
        navigate('/crm/lead-source-master')
      } else {
        toast.error(response.message || 'Failed to create lead source')
      }
    } catch (error) {
      console.error('Error creating lead source:', error)
      toast.error('Failed to create lead source')
    }
  }

  const handleReset = () => {
    setFormData({
      leadSourceCode: '',
      leadSourceName: '',
      leadSourceDescription: '',
      leadSourceCategory: 'Digital Marketing',
      leadSourceType: 'Online',
      leadSourceStatus: 'Active',
      isLeadSourceActive: 'A',
      costPerLead: 0,
      expectedConversionRate: 0,
      averageDealValue: 0,
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      targetRegions: [],
      targetIndustries: [],
      targetCompanySize: 'All',
      totalLeadsGenerated: 0,
      totalLeadsConverted: 0,
      totalRevenue: 0,
      lastLeadDate: '',
      trackingCode: '',
      utmSource: '',
      utmMedium: '',
      utmCampaign: '',
      leadQualityScore: 5,
      leadVolumeScore: 5,
      costEffectivenessScore: 5,
      notes: '',
      tags: []
    })
    setTargetRegions([''])
    setTargetIndustries([''])
    setTags([''])
    loadLeadSourceCode()
  }

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Source Master',
      path: '/crm/lead-source-master',
      onClick: () => navigate('/crm/lead-source-master')
    },
    {
      label: 'New Lead Source',
      path: '/crm/lead-source-master/new',
      isCurrent: true
    }
  ]

  const helpContent = (
    <>
      <h3>What is Lead Source Master?</h3>
      <p>Lead Source Master lets you <strong>create and manage lead source information</strong>—where your leads come from (e.g. website, referrals, campaigns). You can track category, type, cost per lead, conversion rates, contact details, targeting, and performance metrics.</p>
      <h3>Tabs</h3>
      <p><strong>Lead Source Info</strong> – Code, name, description, category, type, status, cost/ROI fields, and optional contact and tracking (UTM) details.</p>
      <p><strong>Targeting &amp; Metrics</strong> – Target regions/industries, company size, and performance metrics (leads generated/converted, revenue, quality scores).</p>
      <p><strong>Notes &amp; Tags</strong> – Additional notes and tags for filtering.</p>
      <h3>How to use</h3>
      <p>Fill required fields (e.g. lead source code, name). Use <strong>Save</strong> to create. Use <strong>Reset</strong> to clear or navigate back to cancel.</p>
    </>
  )

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Lead Source Master Entry</h1>
          <p>Create and manage lead source information</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Lead Source Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'leadSourceInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('leadSourceInfo')}
          >
            <Target size={18} />
            <span>Lead Source Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <BarChart3 size={18} />
            <span>Performance Metrics</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
            onClick={() => setActiveTab('tracking')}
          >
            <Globe size={18} />
            <span>Tracking & Integration</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'targeting' ? 'active' : ''}`}
            onClick={() => setActiveTab('targeting')}
          >
            <Users size={18} />
            <span>Targeting & Demographics</span>
          </button>
        </div>
          
        <form className="form-content">

          {/* Lead Source Info Tab */}
          {activeTab === 'leadSourceInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Lead Source Information</h3>
              </div>
              
              {/* Row 1: Lead Source Code, Name, Category, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadSourceCode" className="form-label required">
                    Lead Source Code
                  </label>
                  <input
                    type="text"
                    id="leadSourceCode"
                    name="leadSourceCode"
                    value={isLoadingLeadSourceCode ? 'Generating...' : formData.leadSourceCode}
                    readOnly
                    className="form-input"
                    placeholder="Enter lead source code"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceName" className="form-label required">
                    Lead Source Name
                  </label>
                  <input
                    type="text"
                    id="leadSourceName"
                    name="leadSourceName"
                    value={formData.leadSourceName}
                    onChange={(e) => handleInputChange('leadSourceName', e.target.value)}
                    className="form-input"
                    placeholder="Enter lead source name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceCategory" className="form-label required">
                    Category
                  </label>
                  <select
                    id="leadSourceCategory"
                    name="leadSourceCategory"
                    value={formData.leadSourceCategory}
                    onChange={(e) => handleInputChange('leadSourceCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="Traditional Marketing">Traditional Marketing</option>
                    <option value="Referral">Referral</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Event">Event</option>
                    <option value="Cold Outreach">Cold Outreach</option>
                    <option value="Content Marketing">Content Marketing</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceType" className="form-label required">
                    Type
                  </label>
                  <select
                    id="leadSourceType"
                    name="leadSourceType"
                    value={formData.leadSourceType}
                    onChange={(e) => handleInputChange('leadSourceType', e.target.value)}
                    className="form-input"
                  >
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Status, Contact Person, Contact Email, Contact Phone */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadSourceStatus" className="form-label required">
                    Status
                  </label>
                  <select
                    id="leadSourceStatus"
                    name="leadSourceStatus"
                    value={formData.leadSourceStatus}
                    onChange={(e) => handleInputChange('leadSourceStatus', e.target.value)}
                    className="form-input"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Suspended">Suspended</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="contactPerson" className="form-label">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact person name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactPhone" className="form-label">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    className="form-input"
                    placeholder="Enter contact phone"
                  />
                </div>
              </div>

              {/* Row 3: Website, Description */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="website" className="form-label">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="form-input"
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '2 / -1' }}>
                  <label htmlFor="leadSourceDescription" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="leadSourceDescription"
                    name="leadSourceDescription"
                    value={formData.leadSourceDescription}
                    onChange={(e) => handleInputChange('leadSourceDescription', e.target.value)}
                    className="form-input"
                    rows="3"
                    placeholder="Enter lead source description"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Performance Metrics Tab */}
          {activeTab === 'performance' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Performance Metrics</h3>
              </div>
              
              {/* Row 1: Cost Per Lead, Expected Conversion Rate, Average Deal Value */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="costPerLead" className="form-label">
                    Cost Per Lead
                  </label>
                  <input
                    type="number"
                    id="costPerLead"
                    name="costPerLead"
                    value={formData.costPerLead}
                    onChange={(e) => handleInputChange('costPerLead', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="expectedConversionRate" className="form-label">
                    Expected Conversion Rate (%)
                  </label>
                  <input
                    type="number"
                    id="expectedConversionRate"
                    name="expectedConversionRate"
                    value={formData.expectedConversionRate}
                    onChange={(e) => handleInputChange('expectedConversionRate', parseInt(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="averageDealValue" className="form-label">
                    Average Deal Value
                  </label>
                  <input
                    type="number"
                    id="averageDealValue"
                    name="averageDealValue"
                    value={formData.averageDealValue}
                    onChange={(e) => handleInputChange('averageDealValue', parseFloat(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastLeadDate" className="form-label">
                    Last Lead Date
                  </label>
                  <input
                    type="date"
                    id="lastLeadDate"
                    name="lastLeadDate"
                    value={formData.lastLeadDate}
                    onChange={(e) => handleInputChange('lastLeadDate', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Row 2: Quality Scores */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadQualityScore" className="form-label">
                    Lead Quality Score (1-10)
                  </label>
                  <input
                    type="number"
                    id="leadQualityScore"
                    name="leadQualityScore"
                    value={formData.leadQualityScore}
                    onChange={(e) => handleInputChange('leadQualityScore', parseInt(e.target.value) || 1)}
                    className="form-input"
                    placeholder="5"
                    min="1"
                    max="10"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadVolumeScore" className="form-label">
                    Lead Volume Score (1-10)
                  </label>
                  <input
                    type="number"
                    id="leadVolumeScore"
                    name="leadVolumeScore"
                    value={formData.leadVolumeScore}
                    onChange={(e) => handleInputChange('leadVolumeScore', parseInt(e.target.value) || 1)}
                    className="form-input"
                    placeholder="5"
                    min="1"
                    max="10"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="costEffectivenessScore" className="form-label">
                    Cost Effectiveness Score (1-10)
                  </label>
                  <input
                    type="number"
                    id="costEffectivenessScore"
                    name="costEffectivenessScore"
                    value={formData.costEffectivenessScore}
                    onChange={(e) => handleInputChange('costEffectivenessScore', parseInt(e.target.value) || 1)}
                    className="form-input"
                    placeholder="5"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tracking & Integration Tab */}
          {activeTab === 'tracking' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Tracking & Integration</h3>
              </div>
              
              {/* Row 1: Tracking Code, UTM Source, UTM Medium, UTM Campaign */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="trackingCode" className="form-label">
                    Tracking Code
                  </label>
                  <input
                    type="text"
                    id="trackingCode"
                    name="trackingCode"
                    value={formData.trackingCode}
                    onChange={(e) => handleInputChange('trackingCode', e.target.value)}
                    className="form-input"
                    placeholder="Enter tracking code"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="utmSource" className="form-label">
                    UTM Source
                  </label>
                  <input
                    type="text"
                    id="utmSource"
                    name="utmSource"
                    value={formData.utmSource}
                    onChange={(e) => handleInputChange('utmSource', e.target.value)}
                    className="form-input"
                    placeholder="Enter UTM source"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="utmMedium" className="form-label">
                    UTM Medium
                  </label>
                  <input
                    type="text"
                    id="utmMedium"
                    name="utmMedium"
                    value={formData.utmMedium}
                    onChange={(e) => handleInputChange('utmMedium', e.target.value)}
                    className="form-input"
                    placeholder="Enter UTM medium"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="utmCampaign" className="form-label">
                    UTM Campaign
                  </label>
                  <input
                    type="text"
                    id="utmCampaign"
                    name="utmCampaign"
                    value={formData.utmCampaign}
                    onChange={(e) => handleInputChange('utmCampaign', e.target.value)}
                    className="form-input"
                    placeholder="Enter UTM campaign"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Targeting & Demographics Tab */}
          {activeTab === 'targeting' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Targeting & Demographics</h3>
              </div>
              
              {/* Row 1: Target Company Size */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="targetCompanySize" className="form-label">
                    Target Company Size
                  </label>
                  <select
                    id="targetCompanySize"
                    name="targetCompanySize"
                    value={formData.targetCompanySize}
                    onChange={(e) => handleInputChange('targetCompanySize', e.target.value)}
                    className="form-input"
                  >
                    <option value="All">All</option>
                    <option value="Startup">Startup</option>
                    <option value="SMB">SMB</option>
                    <option value="Mid-Market">Mid-Market</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
              </div>

              {/* Target Regions */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <MapPin size={20} className="text-blue-500" />
                    <h4>Target Regions</h4>
                    <span className="field-count">{targetRegions.filter(r => r.trim()).length} regions</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetRegion}
                  >
                    <Plus size={16} />
                    Add Region
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetRegions.map((region, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={region}
                          onChange={(e) => handleTargetRegionChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target region (e.g., North India, South India)"
                        />
                        <div className="field-actions">
                          {targetRegions.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetRegion(index)}
                              title="Remove region"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Target Industries */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Users size={20} className="text-green-500" />
                    <h4>Target Industries</h4>
                    <span className="field-count">{targetIndustries.filter(i => i.trim()).length} industries</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTargetIndustry}
                  >
                    <Plus size={16} />
                    Add Industry
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {targetIndustries.map((industry, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={industry}
                          onChange={(e) => handleTargetIndustryChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter target industry (e.g., Technology, Healthcare, Finance)"
                        />
                        <div className="field-actions">
                          {targetIndustries.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTargetIndustry(index)}
                              title="Remove industry"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="dynamic-field-section">
                <div className="dynamic-field-header">
                  <div className="dynamic-field-title">
                    <Tag size={20} className="text-purple-500" />
                    <h4>Tags</h4>
                    <span className="field-count">{tags.filter(t => t.trim()).length} tags</span>
                  </div>
                  <button 
                    type="button" 
                    className="btn-add-field"
                    onClick={addTag}
                  >
                    <Plus size={16} />
                    Add Tag
                  </button>
                </div>
                
                <div className="dynamic-field-list">
                  {tags.map((tag, index) => (
                    <div key={index} className="dynamic-field-item">
                      <div className="field-input-container">
                        <input
                          type="text"
                          value={tag}
                          onChange={(e) => handleTagChange(index, e.target.value)}
                          className="form-input"
                          placeholder="Enter tag (e.g., High Priority, Q4 Campaign)"
                        />
                        <div className="field-actions">
                          {tags.length > 1 && (
                            <button 
                              type="button" 
                              className="btn-remove-field"
                              onClick={() => removeTag(index)}
                              title="Remove tag"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="grid-row-4">
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="notes" className="form-label">
                    <div className="label-with-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14,2 14,8 20,8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10,9 9,9 8,9"></polyline>
                      </svg>
                      Additional Notes
                    </div>
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="form-input"
                    rows="4"
                    placeholder="Enter additional notes about targeting preferences, special requirements, or any other relevant information..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - Available in all tabs */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary"
            >
              <Save size={16} />
              Save
            </button>
          </div>
        </form>
      </div>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Lead Source Master Entry – Help">
        {helpContent}
      </HelpModal>
    </div>
  )
}

export default LeadSourceMasterForm
